<script setup lang="ts">
import CSearchInputDate from '@/components/CSearchInputDate.vue'
import {
  IconArrowsUpDown,
  IconArrowNarrowUp,
  IconArrowNarrowDown,
  IconPlus,
  IconColumns3,
  IconCaretDownFilled,
  IconCaretUpFilled,
  IconFilterX,
  IconInfoCircle
} from '@tabler/icons-vue'
const { t } = useI18n()
const { colKeyToLabel } = useStringFormat()

import { cilSpreadsheet } from '@coreui/icons'

const props = defineProps({
  activePage: {
    type: Number,
    default: 1,
    required: false
  },
  addNew: Boolean,
  columns: {
    type: Array,
    default: () => []
  },
  disableSelectAll: {
    type: Boolean,
    default: false
  },
  exceptionsArray: {
    type: Array,
    default: () => []
  },
  forceExceptionsArray: { type: Boolean, default: false },
  footer: Boolean,
  header: Boolean,
  haveDetails: {
    type: Boolean,
    default: false
  },
  setAllAsArrayOfIds: { type: Boolean, default: false },
  importFromTemplate: Boolean,
  items: {
    type: Array,
    default: () => []
  },
  paginationLinks: {
    type: Array,
    default: () => []
  },
  section: {
    type: String,
    default: '',
    required: false
  },
  selectable: Boolean,
  selectedArray: {
    type: Array,
    default: () => []
  },
  tableProps: {
    type: Object,
    default: function () {
      return {
        striped: false,
        hover: false,
        stripedColumns: false,
        shadowOff: false
      }
    }
  },
  total: {
    type: Number,
    default: 0
  },
  tableDisableSearch: {
    type: Boolean,
    default: false
  },
  selectedException: {
    type: Boolean,
    default: false,
    required: false
  }
})

const emit = defineEmits(['paginationEvent', 'queryEvent', 'newOneEvent', 'importFromTemplateEvent'])

const acceptedColumns = computed(() => {
  let acceptedColumns = props.columns
  if (props.columns.length < 1) {
    for (const key in props.items[1]) {
      let newColumn = {
        key: key,
        label: colKeyToLabel(key)
      }
      acceptedColumns.push(newColumn)
    }
  }
  acceptedColumns = acceptedColumns.filter(element => !element.detail)
  return acceptedColumns
})

const detailColumns = computed(() => {
  return props.columns.filter(element => element.detail)
})

const getDetailLabel = key => {
  const targetIndex = detailColumns.value.findIndex(details => details.key === key)
  return detailColumns.value[targetIndex].label ?? key
}

const acceptedDetails = computed(() => {
  let detailsOrdered = {}
  if (detailColumns.value.length > 0) {
    props.items.forEach(item => {
      let orderedItem = {}
      detailColumns.value.forEach(detail => {
        orderedItem[detail.key] = item[detail.key]
      })
      detailsOrdered[item.id] = orderedItem
    })
  }
  return detailsOrdered
})

const acceptedItems = computed(() => {
  let itemsOrdered = props.items

  if (acceptedColumns.value.length > 0) {
    itemsOrdered = []
    props.items.forEach(item => {
      let orderedItem = { id: item.id }
      acceptedColumns.value.forEach(column => {
        const keys = column.key.split('.') // Dividir la cadena en partes
        let value = item
        // Acceder a las propiedades anidadas
        for (const key of keys) {
          if (value && value.hasOwnProperty(key)) {
            value = value[key]
          } else {
            // Manejar casos en los que la propiedad no existe
            value = null
            break
          }
        }

        orderedItem[column.key] = value
      })
      itemsOrdered.push(orderedItem)
    })
  }
  return itemsOrdered
})

const pagination = computed(() => {
  const pagination = []
  props.paginationLinks.forEach(element => {
    pagination.push({
      label: element.label,
      url: element.url,
      active: element.active,
      disabled: element.url == null
    })
  })
  return pagination
})

const columnLabel = col => {
  if (col.label !== undefined) return col.label
  return colKeyToLabel(col.key)
}

const columnSort = col => {
  if (col.sort !== undefined && col.sort === false) return false
  return true
}

const columnKeySearch = col => {
  if (col.keySearch) return col.keySearch
  return col.key
}

const columnKeySort = col => {
  if (col.keySort) return col.keySort
  return columnKeySearch(col)
}

const cellStyle = item => {
  if (item && '_style' in item) {
    let style = {}
    for (let key in item._style) {
      style[key] = item._style[key]
    }
    return style
  }
  return {}
}

const hiddenColumnByDefault = cellStyle => {
  if (cellStyle?.display && cellStyle?.display === 'none') {
    return true
  }
  return false
}

const queryTimer = ref({})
const handleQuery = (e, colKey) => {
  const typingInterval = 2000
  clearTimeout(queryTimer.value[colKey])
  queryTimer.value[colKey] = setTimeout(() => {
    emit('queryEvent', colKey, e.target.value)
  }, typingInterval)
}

const handleInstantQuery = (e, colKey) => {
  if (e.key === 'Enter') {
    clearTimeout(queryTimer.value[colKey])
    emit('queryEvent', colKey, e.target.value)
  }
}
const colSort = ref([])
const sortTable = colKey => {
  emit('queryEvent', colKey, 'sortTableEvent')
  setIconForSort(colKey)
}
const setIconForSort = colKey => {
  const target = colSort.value.find(element => element && element.name === colKey)
  if (target) {
    if (target?.order === null) target.order = 'ASC'
    else if (target?.order === 'ASC') target.order = 'DESC'
    else if (target?.order === 'DESC') target.order = null
  } else {
    colSort.value.push({
      name: colKey,
      order: 'ASC'
    })
  }
}
const getIconForSort = colKey => {
  const target = colSort.value.find(element => element && element.name === colKey)

  if (target && target?.order) {
    return target.order
  }
  return null
}

const getCol = (acceptedColumns, keyCol) => acceptedColumns.filter(column => column.key === keyCol).pop()

function getProperty(item, key) {
  if (key.includes('.')) {
    // Dividir la clave en partes
    const keys = key.split('.')
    // Acceder a la propiedad anidada
    let value = item
    for (const k of keys) {
      if (value !== null && value[k] !== undefined) {
        value = value[k]
      } else {
        value = ''
        break
      }
    }
    return value
  } else {
    // Si no hay anidamiento, acceder directamente a la propiedad
    return item[key] || ''
  }
}

const showingRows = ref([])
const toggleDetailsRow = row => {
  const index = showingRows.value.indexOf(row)
  if (index === -1) {
    showingRows.value.push(row)
  } else {
    showingRows.value.splice(index, 1)
  }
}
const showRows = row => {
  const index = showingRows.value.indexOf(row)
  return index !== -1
}

const fillArraySelectable = event => {
  const rawValue = event.target.value
  const parsedValue = Number(rawValue)
  const value = isNaN(parsedValue) ? rawValue : parsedValue

  if (event.target.checked) {
    ifValueIsAllWillEmptySelectedArrayCheckAllAndPushValue(value)
    ifValueIsNotAllButAllIsCheckedWillDeleteFromExceptions(value)
    ifValueIsIDAndAllIsNotCheckedWillPushToSelectedArray(value)
    if (props.forceExceptionsArray) {
      forceEmptyExceptionsArray(value)
    }
  } else {
    const findIndex = props.selectedArray.findIndex(element => element === value)
    if (findIndex > -1) props.selectedArray.splice(findIndex, 1)
    ifValueIsAllEmptySelectedArrayAndExceptionsArrayAndUncheckAll(value)
    ifValueIsNotAndAllIsSelectedFillExceptionArray(value)
    if (props.forceExceptionsArray) {
      forceFillExceptionsArray(value)
    }
  }
}

function forceFillExceptionsArray(value) {
  const existInExceptionArray = props.exceptionsArray.findIndex(element => element === value)
  if (existInExceptionArray < 0) props.exceptionsArray.push(value)
}

function forceEmptyExceptionsArray(value) {
  const existInExceptionArray = props.exceptionsArray.findIndex(element => element === value)
  if (existInExceptionArray > -1) props.exceptionsArray.splice(existInExceptionArray, 1)
}

function ifValueIsAllWillEmptySelectedArrayCheckAllAndPushValue(value) {
  if (value === 'all') {
    checkAll()
    if (props.setAllAsArrayOfIds) {
      putAllCheckedIntoSelectedArray()
    } else {
      props.selectedArray.splice(0, props.selectedArray.length)
      props.selectedArray.push(value)
    }
  }
}

function ifValueIsNotAllButAllIsCheckedWillDeleteFromExceptions(value) {
  const foundAll = props.selectedArray.findIndex(element => element === 'all')
  if (foundAll > -1 && value !== 'all') {
    const exceptionIndex = props.exceptionsArray.findIndex(element => element === value)
    if (exceptionIndex > -1) props.exceptionsArray.splice(exceptionIndex, 1)
  }
}

function ifValueIsIDAndAllIsNotCheckedWillPushToSelectedArray(value) {
  const foundAll = props.selectedArray.findIndex(element => element === 'all')
  if (foundAll < 0 && value !== 'all') {
    const idIndex = props.selectedArray.findIndex(element => element === value)
    if (idIndex < 0) props.selectedArray.push(Number(value)) // Convertimos a número
  }
}

function ifValueIsNotAndAllIsSelectedFillExceptionArray(value) {
  const foundAll = props.selectedArray.findIndex(element => element === 'all')
  if (foundAll > -1 && value !== 'all') {
    const existInExceptionArray = props.exceptionsArray.findIndex(element => element === value)
    if (existInExceptionArray < 0) props.exceptionsArray.push(value)
  }
}

function ifValueIsAllEmptySelectedArrayAndExceptionsArrayAndUncheckAll(value) {
  if (value === 'all') {
    props.selectedArray.splice(0, props.selectedArray.length)
    props.exceptionsArray.splice(0, props.exceptionsArray.length)
    uncheckAll()
  }
}

function checkAll() {
  document.querySelectorAll('input[type="checkbox"].table-checkbox').forEach(element => (element.checked = true))
}

function putAllCheckedIntoSelectedArray() {
  document.querySelectorAll('input[type="checkbox"].table-checkbox').forEach(element => {
    if (element.checked && element.value && element.value !== 'on' && element.value !== 'all') {
      props.selectedArray.push(element.value)
    }
  })
}

function uncheckAll() {
  document.querySelectorAll('input[type="checkbox"].table-checkbox').forEach(element => (element.checked = false))
}

function isChecked(value) {
  const findIndex = props.selectedArray.findIndex(element => element === Number(value))
  const allFound = props.selectedArray.findIndex(element => element === 'all')
  const exceptionFound = props.exceptionsArray.findIndex(element => element === value)
  if (findIndex > -1 || (allFound > -1 && exceptionFound < 0)) {
    return true
  }
  return false
}

const sectionConfiguration = ref(loadConfigurationOfSection())
const showHideColumn = ref(false)

function handleHideColumn(event, value) {
  if (!event.target.checked) {
    sectionConfiguration.value.hiddenColumns.push(value)
  } else {
    const index = sectionConfiguration.value.hiddenColumns.findIndex(element => element === value)
    if (index > -1) sectionConfiguration.value.hiddenColumns.splice(index, 1)
  }
  saveConfigurationOfSection()
}

function checkedHiddenColumn(value) {
  if (sectionConfiguration.value.hiddenColumns.includes(value)) return false
  return true
}

function loadConfigurationOfSection() {
  let storage = localStorage.getItem('AppConfig')
  if (storage) {
    storage = JSON.parse(storage)
    const nameOfSection = props.section.length > 1 ? props.section : window.location.pathname
    if (storage[nameOfSection]) return storage[nameOfSection]
  }
  return {
    hiddenColumns: []
  }
}

function saveConfigurationOfSection() {
  let storage = localStorage.getItem('AppConfig')
  storage ? (storage = JSON.parse(storage)) : (storage = {})

  const nameOfSection = props.section.length > 1 ? props.section : window.location.pathname
  storage[nameOfSection] = sectionConfiguration.value
  localStorage.setItem('AppConfig', JSON.stringify(storage))
}

function filterAcceptedColumns(acceptedColumns) {
  return acceptedColumns
}

function emitDropdownQueryEvent(column, value) {
  emit('queryEvent', column, value)
}

const refCols = ref([])

function labelDropdownt(col) {
  if (col.key === 'groups') {
    const index = refCols.value.find(item => item.active === true)
    if (index) return index.label
    return 'Options'
  } else {
    const index = col.list.find(item => item.active === true)
    if (index) return index.label
    return 'Options'
  }
}

function ClearDropActive(list) {
  list.forEach(element => (element.active = false))
}

const resetFilters = async () => {
  cleanLabelsDropDwownt()
  emit('queryEvent', null, '')
}

const cleanLabelsDropDwownt = () => {
  return acceptedColumns.value.map(item => {
    if (item.list) {
      item.list = item.list.map(({ active, ...rest }) => rest)
    }
    return item
  })
}

const toDelete = id => {
  if (!props.selectedException) return null
  if (props.selectedArray.includes(String(id))) return 'danger'
  else return null
}
</script>

<template>
  <div class="row">
    <div class="col">
      <div class="card mb-2" :class="{ 'shadow-none': tableProps?.shadowOff, 'border-0': tableProps?.shadowOff }">
        <div class="card-body p-3">
          <div class="row">
            <div class="col">
              <div class="d-flex">
                <div class="card-title fs-4 fw-semibold">{{ t(section) }}</div>
                <div v-if="total" class="card-subtitle fw-normal text-disabled ms-5" style="padding-top: 12px">
                  {{ total }} {{ t('registered') }}
                </div>
                <CButton
                  color="link"
                  variant="outline"
                  size="sm"
                  :class="{ 'ms-5': section, 'text-disabled': !showHideColumn }"
                  style="padding-top: 0px"
                  @click="showHideColumn = !showHideColumn"
                >
                  <div class="d-flex align-items-center">
                    <IconColumns3 size="15" class="me-1" />
                    {{ t('Showing columns') }}
                    <IconCaretUpFilled v-if="showHideColumn" size="20" stroke-width="1" />
                    <IconCaretDownFilled v-else size="20" stroke-width="1" />
                  </div>
                </CButton>
                <button
                  v-if="!tableDisableSearch"
                  @click="resetFilters"
                  class="btn btn-sm btn-link ms-4 mb-1"
                  style="text-decoration: none; color: #8d8d8d"
                >
                  <IconFilterX size="15" />
                  {{ t('Reset filters') }}
                </button>
              </div>
            </div>
            <slot name="buttonsHeader">
              <CCol xs="auto" class="ms-auto" v-if="importFromTemplate || addNew">
                <CButton
                  class="me-3 mb-3"
                  color="secondary"
                  @click="$emit('importFromTemplateEvent')"
                  v-if="importFromTemplate"
                >
                    <slot name="importFromTemplate">
                        <CIcon :icon="cilSpreadsheet" />
                        {{ t('Import from template') }}
                    </slot>
                </CButton>
                <CButton v-if="addNew" color="secondary" class="mb-3" @click="$emit('newOneEvent')">
                  <slot name="addNewButton">
                    <IconPlus class="me-2" size="18" />
                    {{ t('Add new') }}
                  </slot>
                </CButton>
              </CCol>
            </slot>
            <CCol xs="auto" class="ms-auto" v-if="!importFromTemplate">
              <slot name="buttonsHead"></slot>
            </CCol>
          </div>
          <Transition name="height">
            <div v-show="showHideColumn" class="px-3 py-0">
              <div class="card">
                <div class="card-body">
                  <div class="row">
                    <div v-for="(col, index) in acceptedColumns" :key="index" :xs="12" md="2" class="col mb-1">
                      <div class="form-check" v-if="!hiddenColumnByDefault(cellStyle(col))">
                        <input
                          class="form-check-input table-checkbox"
                          type="checkbox"
                          :id="col.key"
                          @change="event => handleHideColumn(event, col.key)"
                          :checked="checkedHiddenColumn(col.key)"
                        />
                        <label class="form-check-label" :for="col.key">
                          {{ col.key === 'actions' || col.key === 'action' ? t('Actions') : t(columnLabel(col)) }}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
          <CTable
            align="middle"
            class="mb-0 mt-2 min-315px"
            :hover="tableProps.hover"
            :striped="tableProps.striped"
            :stripedColumns="tableProps.stripedColumns"
            responsive
          >
            <CTableHead class="fw-semibold" v-if="header">
              <CTableRow>
                <td v-if="!disableSelectAll && selectable">
                  <div class="form-check mb-2">
                    <input
                      class="form-check-input table-checkbox"
                      type="checkbox"
                      value="all"
                      style="transform: scale(1.5)"
                      @change="event => fillArraySelectable(event)"
                      :checked="Boolean(isChecked('all'))"
                    />
                  </div>
                </td>
                <td scope="col" v-if="selectable && disableSelectAll" style="vertical-align: middle"></td>
                <slot
                  v-for="col in acceptedColumns.filter(
                    element => !sectionConfiguration.hiddenColumns.includes(element.key)
                  )"
                  :key="col.key"
                  :name="`${col.key}Header`"
                  :item="col"
                >
                  <CTableHeaderCell scope="col" :style="cellStyle(col)">
                    <table style="width: 100%">
                      <thead>
                        <tr>
                          <td :style="cellStyle(col)" style="height: 43px !important; position: relative">
                            <div v-if="col._tool_tip">
                              <CLink v-c-tooltip="t(col._tool_tip)" class="table_tool_tip">
                                <IconInfoCircle stroke-width="2" size="16" />
                              </CLink>
                            </div>
                            {{ t(columnLabel(col)) }}
                          </td>
                          <td
                            v-show="columnSort(col) && !tableDisableSearch"
                            style="vertical-align: bottom; width: 48px; text-align: center"
                          >
                            <CButton variant="ghost" @click="sortTable(columnKeySort(col))">
                              <IconArrowsUpDown
                                size="14"
                                class="mb-2"
                                v-if="getIconForSort(columnKeySort(col)) === null"
                              />
                              <IconArrowNarrowDown
                                size="14"
                                class="mb-2"
                                style="color: #549d54"
                                v-if="getIconForSort(columnKeySort(col)) === 'ASC'"
                              />
                              <IconArrowNarrowUp
                                size="14"
                                class="mb-2"
                                style="color: #e66464"
                                v-if="getIconForSort(columnKeySort(col)) === 'DESC'"
                              />
                            </CButton>
                          </td>
                        </tr>
                      </thead>
                    </table>
                  </CTableHeaderCell>
                </slot>
              </CTableRow>
              <CTableRow v-if="!tableDisableSearch">
                <CTableDataCell scope="col" v-if="selectable"></CTableDataCell>
                <slot
                  v-for="col in acceptedColumns.filter(
                    element => !sectionConfiguration.hiddenColumns.includes(element.key)
                  )"
                  :key="col.key"
                >
                  <CTableDataCell v-if="!col.disableSearch" :style="cellStyle(col)">
                    <CFormInput
                      v-if="col.search !== 'date' && col.search !== 'enum'"
                      :type="col.search ?? 'text'"
                      size="sm"
                      @input="
                        e => {
                          handleQuery(e, columnKeySearch(col))
                        }
                      "
                      @keypress="
                        e => {
                          handleInstantQuery(e, columnKeySearch(col))
                        }
                      "
                    ></CFormInput>

                    <div class="d-grid gap-1">
                      <CSearchInputDate
                        v-if="col.search === 'date'"
                        :col="col"
                        color="secondary"
                        @dateQueryComponent="handleQuery"
                        @dateInstantQueryComponent="handleInstantQuery"
                      ></CSearchInputDate>

                      <CDropdown
                        v-if="col.search === 'enum'"
                        auto-close="outside"
                        direction="center"
                        placement="bottom"
                      >
                        <CDropdownToggle component="a" color="secondary" size="sm">
                          {{ t(labelDropdownt(col)) }}
                        </CDropdownToggle>
                        <CDropdownMenu>
                          <CDropdownItem
                            @click="
                              () => {
                                $emit('queryEvent', columnKeySearch(col), '')
                                ClearDropActive(col.list)
                                refCols = []
                              }
                            "
                          >
                            {{ t('All') }}
                          </CDropdownItem>
                          <template v-if="col.list && col.list.length > 0">
                            <CDropdownItem
                              v-for="l in col.list"
                              @click="
                                () => {
                                  if (columnKeySearch(col) === 'group_name')
                                    emitDropdownQueryEvent(columnKeySearch(col), l.label)
                                  else emitDropdownQueryEvent(columnKeySearch(col), l.value)
                                  ClearDropActive(col.list)
                                  l.active = true
                                  if (col.key == 'groups') refCols = col.list
                                }
                              "
                              :key="l.key"
                            >
                              {{ t(l.label) }}
                            </CDropdownItem>
                          </template>
                        </CDropdownMenu>
                      </CDropdown>
                    </div>
                  </CTableDataCell>
                  <CTableDataCell v-else></CTableDataCell>
                </slot>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              <CTableRow v-if="items.length === 0">
                <CTableDataCell class="border-0 text-center text-muted py-5" colspan="100%">
                  <div class="py-5">{{ t('No items found') }}</div>
                </CTableDataCell>
              </CTableRow>
              <template v-for="(row, keyRow) in items" :key="keyRow">
                <!--                <CTableRow :color="toDelete(row.id) ? 'danger' : ''"  >-->
                <CTableRow :color="toDelete(row.id)">
                  <CTableDataCell scope="col" v-if="selectable">
                    <div class="form-check">
                      <!--                      {{row.id}}-->
                      <input
                        class="table-checkbox form-check-input"
                        type="checkbox"
                        style="transform: scale(1.3)"
                        :value="String(row.id)"
                        @change="event => fillArraySelectable(event)"
                        :checked="isChecked(row.id)"
                      />
                    </div>
                  </CTableDataCell>
                  <template v-for="(item, keyCol) in filterAcceptedColumns(acceptedItems[keyRow])" :key="keyCol">
                    <slot
                      :name="keyCol"
                      :item="acceptedItems[keyRow]"
                      :keyRow="keyRow"
                      v-if="!sectionConfiguration.hiddenColumns.includes(keyCol)"
                    >
                      <CTableDataCell v-if="keyCol !== 'id'" :style="cellStyle(getCol(acceptedColumns, keyCol))">
                        {{ getProperty(row, keyCol) }}
                        <!-- {{ item }} -->
                      </CTableDataCell>
                    </slot>
                  </template>
                  <CTableDataCell v-if="haveDetails">
                    <CButton
                      color="secondary"
                      @click="toggleDetailsRow(keyRow)"
                      size="sm"
                      v-c-tooltip="{ content: t('More details'), placement: 'top' }"
                    >
                      <IconCaretUpFilled v-if="showingRows.includes(keyRow)" size="18" stroke-width="1" />
                      <IconCaretDownFilled v-else d size="18" stroke-width="1" />
                    </CButton>
                  </CTableDataCell>
                </CTableRow>

                <CTableRow v-if="haveDetails">
                  <CTableDataCell :colspan="acceptedColumns.length + 1" class="p-0">
                    <Transition name="height">
                      <div v-if="showRows(keyRow)" class="px-3 py-3">
                        <slot name="details" :item="acceptedDetails[acceptedItems[keyRow].id]">
                          <p v-for="(detail, index) in acceptedDetails[acceptedItems[keyRow].id]" :key="index">
                            <span class="fw-medium me-3">{{ t(getDetailLabel(index)) }}:</span>
                            <span>{{ detail }}</span>
                          </p>
                        </slot>
                      </div>
                    </Transition>
                  </CTableDataCell>
                </CTableRow>
              </template>
            </CTableBody>
            <CTableFoot v-if="footer">
              <CTableRow>
                <slot name="footer"></slot>

                <!-- <slot
                  v-for="col in acceptedColumns.filter(
                    element => !sectionConfiguration.hiddenColumns.includes(element.key)
                  )"
                  :key="col.key"
                  :name="`${col.key}Footer`"
                  :item="col"
                >
                  <CTableHeaderCell scope="col" :style="cellStyle(col)">{{ columnLabel(col) }}</CTableHeaderCell>
                </slot> -->
              </CTableRow>
            </CTableFoot>
          </CTable>
          <div class="row mt-4">
            <div class="col d-flex justify-content-end">
              <div>
                <slot name="buttons"></slot>
              </div>
            </div>
          </div>
          <div class="row mt-4">
            <div class="col">
              <CPagination :aria-label="`${section} navigation`">
                <CPaginationItem
                  v-for="(pag, index) in pagination"
                  :key="index"
                  :active="pag.active"
                  href="#"
                  @click="
                    e => {
                      e.preventDefault()
                      if (!pag.disabled) {
                        $emit('paginationEvent', pag.url)
                      }
                    }
                  "
                  :data-url="pag.url"
                  :disabled="pag.disabled"
                >
                  <span v-html="t(pag.label)"></span>
                </CPaginationItem>
              </CPagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
